<script setup lang="ts">
import { computed } from "vue"

export type DrinkLayer = {
    key: string
    label: string
    color: string
    height: number
    side: "left" | "right"
    texture?: string
}

const props = defineProps<{
    recipeId: number | string
    isIced: boolean
    category: string
    layers: DrinkLayer[]
}>()

const CUP_TOP = 112
const CUP_BOTTOM = 332
const CUP_HEIGHT = CUP_BOTTOM - CUP_TOP

const clipId = computed(() => `cup-clip-${props.recipeId}`)
const shineId = computed(() => `cup-shine-${props.recipeId}`)
const foamId = computed(() => `cup-foam-${props.recipeId}`)

const isTopping = (layer: DrinkLayer) => ["whip", "drizzle", "dust"].includes(layer.texture ?? "")

const interiorLayers = computed(() => {
    const interior = props.layers.filter((layer) => !isTopping(layer) && layer.texture !== "chips")
    const total = interior.reduce((sum, layer) => sum + Number(layer.height || 0), 0) || 1

    let cursor = CUP_BOTTOM
    return interior.map((layer) => {
        const height = (Number(layer.height) / total) * CUP_HEIGHT
        const y = cursor - height
        const item = { ...layer, y, heightPx: height, midY: y + height / 2 }
        cursor = y
        return item
    })
})

const toppingLayers = computed(() => props.layers.filter((layer) => isTopping(layer)))

const stackedLayers = computed(() => {
    const toppings = toppingLayers.value.map((layer, index) => {
        const height = 18
        const y = CUP_TOP - 8 - (index + 1) * 14
        return { ...layer, y, heightPx: height, midY: y + 8 }
    })

    const blended = interiorLayers.value.find((layer) => layer.texture === "blended")
        ?? interiorLayers.value[Math.max(0, interiorLayers.value.length - 1)]

    const chipLabels = props.layers
        .filter((layer) => layer.texture === "chips")
        .map((layer) => ({
            ...layer,
            y: blended?.y ?? CUP_TOP,
            heightPx: blended?.heightPx ?? 20,
            midY: (blended?.y ?? CUP_TOP) + Math.min(24, (blended?.heightPx ?? 40) * 0.25),
        }))

    return [...interiorLayers.value, ...toppings, ...chipLabels]
})

const iceLayer = computed(() => interiorLayers.value.find((layer) => layer.texture === "ice"))
const whipLayer = computed(() => toppingLayers.value.find((layer) => layer.texture === "whip"))
const drizzleLayer = computed(() => toppingLayers.value.find((layer) => layer.texture === "drizzle"))
const hasDome = computed(() => props.category === "Frappuccino")

function xAtY(y: number, side: "left" | "right") {
    if (y < CUP_TOP) {
        return side === "left" ? 134 : 226
    }

    const t = Math.min(1, Math.max(0, (y - CUP_TOP) / CUP_HEIGHT))
    if (side === "left") {
        return 128 + t * 14
    }
    return 232 - t * 14
}

const iceCubes = computed(() => {
    const layer = iceLayer.value
    if (!layer) {
        return []
    }

    const templates = [
        { x: 0.18, y: 0.12, s: 22, r: -16 },
        { x: 0.48, y: 0.08, s: 26, r: 10 },
        { x: 0.28, y: 0.42, s: 20, r: 6 },
        { x: 0.58, y: 0.38, s: 24, r: -8 },
        { x: 0.12, y: 0.62, s: 18, r: 14 },
        { x: 0.42, y: 0.68, s: 21, r: -12 },
        { x: 0.66, y: 0.58, s: 17, r: 18 },
    ]

    const innerLeft = xAtY(layer.midY, "left") + 8
    const innerRight = xAtY(layer.midY, "right") - 8
    const width = Math.max(40, innerRight - innerLeft)

    return templates.map((cube, index) => ({
        id: `${props.recipeId}-ice-${index}`,
        x: innerLeft + cube.x * (width - cube.s),
        y: layer.y + cube.y * Math.max(12, layer.heightPx - cube.s),
        s: cube.s,
        r: cube.r,
    }))
})

const foamBubbles = computed(() => {
    const layer = interiorLayers.value.find((item) => item.texture === "foam")
    if (!layer) {
        return []
    }

    return [
        [0.2, 0.35, 5],
        [0.38, 0.2, 4],
        [0.55, 0.45, 6],
        [0.7, 0.25, 3.5],
        [0.3, 0.7, 4.5],
        [0.62, 0.68, 3],
    ].map(([x, y, r], index) => ({
        id: `${props.recipeId}-bubble-${index}`,
        cx: xAtY(layer.midY, "left") + 10 + x * 70,
        cy: layer.y + y * layer.heightPx,
        r,
    }))
})

const blendedFlecks = computed(() => {
    const layer = interiorLayers.value.find((item) => item.texture === "blended")
    if (!layer) {
        return []
    }

    return Array.from({ length: 14 }, (_, index) => {
        const x = 0.12 + ((index * 17) % 70) / 100
        const y = 0.08 + ((index * 23) % 80) / 100
        return {
            id: `${props.recipeId}-fleck-${index}`,
            x: xAtY(layer.y + y * layer.heightPx, "left") + 12 + x * 55,
            y: layer.y + y * layer.heightPx,
            r: 1.6 + (index % 3),
        }
    })
})

const chipDots = computed(() => {
    const layer = interiorLayers.value.find((item) => item.texture === "chips")
        ?? interiorLayers.value.find((item) => item.texture === "blended")
    if (!layer || !props.layers.some((item) => item.texture === "chips")) {
        return []
    }

    return [
        [0.22, 0.2],
        [0.48, 0.12],
        [0.35, 0.42],
        [0.62, 0.36],
        [0.28, 0.68],
        [0.55, 0.58],
        [0.42, 0.82],
    ].map(([x, y], index) => ({
        id: `${props.recipeId}-chip-${index}`,
        cx: xAtY(layer.midY, "left") + 14 + x * 60,
        cy: layer.y + y * layer.heightPx,
        r: 2.4 + (index % 2),
    }))
})

const cupPath = computed(() => {
    if (props.isIced) {
        return "M128 112 L114 318 Q114 336 136 336 L224 336 Q246 336 246 318 L232 112 Z"
    }

    return "M124 112 L118 320 Q118 340 140 340 L220 340 Q242 340 242 320 L236 112 Z"
})
</script>

<template>
    <svg viewBox="0 0 360 400" class="h-full w-full" aria-hidden="true">
        <defs>
            <clipPath :id="clipId">
                <path :d="cupPath" />
            </clipPath>
            <linearGradient :id="shineId" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stop-color="#ffffff" stop-opacity="0.35" />
                <stop offset="35%" stop-color="#ffffff" stop-opacity="0.05" />
                <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
            </linearGradient>
            <pattern :id="foamId" width="10" height="8" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="3" r="1.1" fill="#ffffff" fill-opacity="0.45" />
                <circle cx="7" cy="6" r="0.9" fill="#ffffff" fill-opacity="0.35" />
            </pattern>
        </defs>

        <!-- Leader lines and labels -->
        <g v-for="layer in stackedLayers" :key="`label-${layer.key}`">
            <polyline
                fill="none"
                stroke="#c41e3a"
                stroke-width="1"
                :points="
                    layer.side === 'left'
                        ? `${xAtY(layer.midY, 'left')},${layer.midY} 108,${layer.midY}`
                        : `${xAtY(layer.midY, 'right')},${layer.midY} 252,${layer.midY}`
                "
            />
            <text
                :x="layer.side === 'left' ? 104 : 256"
                :y="layer.midY + 3.5"
                :text-anchor="layer.side === 'left' ? 'end' : 'start'"
                fill="#1e3932"
                font-size="11"
                font-family="Figtree, Helvetica, sans-serif"
            >
                {{ layer.label }}
            </text>
        </g>

        <!-- Cup fill -->
        <g :clip-path="`url(#${clipId})`">
            <rect
                v-for="layer in interiorLayers.filter((item) => item.texture !== 'chips')"
                :key="`fill-${layer.key}`"
                x="100"
                width="160"
                :y="layer.y"
                :height="layer.heightPx + 0.6"
                :fill="layer.color"
            />
            <rect
                v-for="layer in interiorLayers.filter((item) => item.texture === 'foam')"
                :key="`foam-${layer.key}`"
                x="100"
                width="160"
                :y="layer.y"
                :height="layer.heightPx"
                :fill="`url(#${foamId})`"
            />
            <g v-for="cube in iceCubes" :key="cube.id">
                <rect
                    :x="cube.x"
                    :y="cube.y"
                    :width="cube.s"
                    :height="cube.s * 0.85"
                    rx="3"
                    fill="#eaf7fc"
                    fill-opacity="0.92"
                    stroke="#9fd4ea"
                    stroke-width="1"
                    :transform="`rotate(${cube.r} ${cube.x + cube.s / 2} ${cube.y + cube.s / 2})`"
                />
            </g>
            <circle
                v-for="bubble in foamBubbles"
                :key="bubble.id"
                :cx="bubble.cx"
                :cy="bubble.cy"
                :r="bubble.r"
                fill="#ffffff"
                fill-opacity="0.55"
                stroke="#f2e6d4"
                stroke-width="0.5"
            />
            <circle
                v-for="fleck in blendedFlecks"
                :key="fleck.id"
                :cx="fleck.x"
                :cy="fleck.y"
                :r="fleck.r"
                fill="#ffffff"
                fill-opacity="0.35"
            />
            <circle
                v-for="chip in chipDots"
                :key="chip.id"
                :cx="chip.cx"
                :cy="chip.cy"
                :r="chip.r"
                fill="#3c1f12"
                fill-opacity="0.85"
            />
            <rect x="108" :y="CUP_TOP" width="28" :height="CUP_HEIGHT" :fill="`url(#${shineId})`" />
        </g>

        <!-- Cup outline -->
        <path :d="cupPath" fill="none" stroke="#1e3932" stroke-width="2.4" />
        <ellipse cx="180" :cy="CUP_TOP" rx="53" ry="7" fill="none" stroke="#1e3932" stroke-width="2.2" />
        <path
            v-if="isIced"
            d="M128 112 Q180 98 232 112"
            fill="none"
            stroke="#1e3932"
            stroke-width="2.2"
        />

        <!-- Hot cup sleeve -->
        <g v-if="!isIced">
            <path d="M121 210 L119 248 L241 248 L239 210 Z" fill="#e8d5b5" stroke="#1e3932" stroke-width="1.2" />
            <text x="180" y="236" text-anchor="middle" fill="#1e3932" font-size="8" font-family="Libre Baskerville, Georgia, serif">
                antahan
            </text>
        </g>

        <!-- Whipped cream overflowing the rim -->
        <g v-if="whipLayer">
            <ellipse cx="180" cy="96" rx="48" ry="16" fill="#fffaf0" stroke="#e8d9c3" stroke-width="1" />
            <ellipse cx="158" cy="90" rx="22" ry="14" fill="#fffdf8" stroke="#eadbc4" stroke-width="0.8" />
            <ellipse cx="202" cy="89" rx="20" ry="13" fill="#fffdf8" stroke="#eadbc4" stroke-width="0.8" />
            <ellipse cx="180" cy="80" rx="24" ry="14" fill="#ffffff" stroke="#eadbc4" stroke-width="0.8" />
            <ellipse cx="168" cy="74" rx="12" ry="8" fill="#fffdf8" />
            <ellipse cx="192" cy="73" rx="11" ry="7" fill="#fffdf8" />
        </g>

        <!-- Caramel drizzle -->
        <path
            v-if="drizzleLayer"
            d="M156 68 C164 78, 170 58, 178 72 C186 86, 192 60, 204 74 C210 80, 214 70, 218 76"
            fill="none"
            stroke="#c4782a"
            stroke-width="2.4"
            stroke-linecap="round"
        />

        <!-- Frappuccino dome lid hint -->
        <path
            v-if="hasDome && !whipLayer"
            d="M128 112 Q180 58 232 112"
            fill="#eaf6fb"
            fill-opacity="0.35"
            stroke="#1e3932"
            stroke-width="2"
        />
    </svg>
</template>
